// src/utils/logger.js
// A drop-in replacement for console.* that you can also use to shim window.console

const isDev =
  (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") ||
  (typeof window !== "undefined" && window.__DEV__ === true);

const originalConsole =
  (typeof window !== "undefined" && window.console) ||
  (typeof console !== "undefined" ? console : {});

const DEDUPE_WINDOW_MS = 5000; // collapse identical messages within this window
const seen = new Map(); // key -> expiresAt ms

function keyFor(level, args) {
  try {
    // stringify first arg aggressively; include level & first two args for stability
    const head = args.slice(0, 2).map(a => {
      if (a instanceof Error) return `${a.name}:${a.message}`;
      if (typeof a === "object") return JSON.stringify(a);
      return String(a);
    }).join("|");
    return `${level}:${head}`;
  } catch {
    return `${level}:${String(args[0])}`;
  }
}

function shouldEmit(level, args) {
  const k = keyFor(level, args);
  const now = Date.now();
  const until = seen.get(k) || 0;
  if (until > now) return false;
  seen.set(k, now + DEDUPE_WINDOW_MS);
  return true;
}

// gate which levels are allowed in production
const prodAllow = {
  error: true,
  warn: true,
  info: false,
  log:  false,
  debug: false,
  trace: false,
  group: false,
  groupEnd: false,
};

// if you want warn/error to be silent in prod too, set to false
const prodPrintToConsole = true;

function emit(level, args) {
  const sink = originalConsole[level] ? originalConsole[level].bind(originalConsole) : originalConsole.log?.bind(originalConsole);
  if (!sink) return;

  // prefix with timestamp + level for clarity
  const ts = new Date().toISOString();
  sink(`[${ts}] [${level}]`, ...args);
}

function makeLevel(level) {
  return (...args) => {
    // dev: pass through with dedupe
    if (isDev) {
      if (shouldEmit(level, args)) emit(level, args);
      return;
    }
    // prod: only some levels, also dedupe
    if (!prodAllow[level]) return;
    if (!shouldEmit(level, args)) return;
    if (prodPrintToConsole) emit(level, args);
  };
}

export const logger = {
  log:    makeLevel("log"),
  info:   makeLevel("info"),
  warn:   makeLevel("warn"),
  error:  makeLevel("error"),
  debug:  makeLevel("debug"),
  trace:  makeLevel("trace"),
  group:  makeLevel("group"),
  groupEnd: makeLevel("groupEnd"),

  // Optional: install as the global console shim
  installConsoleShim() {
    if (typeof window === "undefined") return;
    window.console = {
      ...window.console,
      log: this.log,
      info: this.info,
      warn: this.warn,
      error: this.error,
      debug: this.debug,
      trace: this.trace,
      group: this.group,
      groupEnd: this.groupEnd,
    };
  },

  // Optional: allow callers to force-print something (bypass dedupe) in dev
  _force(level, ...args) {
    if (isDev) emit(level, args);
  }
};

export default logger;
